import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  VHF_CALL_TYPES,
  VHF_CHANNELS,
  type VhfCallType,
  type VhfChannel,
} from "@nav-games/comms";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface VhfProps {
  onNavigate: (to: string) => void;
}

const MODULE = "mayday";
const GAME = "vhf-procedure";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type CardKind = "channel" | "call-type";

interface Card {
  kind: CardKind;
  prompt: string;
  channelAnswer?: VhfChannel;
  callTypeAnswer?: VhfCallType;
  choices: ReadonlyArray<{ id: string; label: string }>;
}

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

const CHANNEL_SCENARIOS: ReadonlyArray<{
  channel: number;
  prompt: string;
}> = [
  {
    channel: 16,
    prompt:
      "You need to make an initial call to another vessel you've never spoken to before. Which channel do you START on?",
  },
  {
    channel: 16,
    prompt:
      "You decide to broadcast a MAYDAY by voice (no DSC available). Which channel?",
  },
  {
    channel: 70,
    prompt:
      "Your radio's DSC controller will transmit your distress alert on which channel?",
  },
  {
    channel: 13,
    prompt:
      "You're approaching a tug + tow in a busy estuary and want to coordinate passing arrangements ship-to-ship at low power. Which channel?",
  },
  {
    channel: 67,
    prompt:
      "UK Solent Coastguard has answered your Mayday on Ch 16 and asks you to switch to a SAR working channel. Which one?",
  },
  {
    channel: 80,
    prompt:
      "You're entering a UK marina and need to ask about a berth. Which channel?",
  },
  {
    channel: 23,
    prompt:
      "UK Coastguard's scheduled Maritime Safety Information broadcast (gale warnings, nav warnings) goes out on which channel in many UK regions?",
  },
  {
    channel: 6,
    prompt:
      "You're coordinating safety / SAR working between two vessels and need an international inter-ship channel. Which one?",
  },
  {
    channel: 8,
    prompt:
      "After a routine initial call on Ch 16 you want to switch to a general international inter-ship working channel. Pick one.",
  },
  {
    channel: 12,
    prompt:
      "A commercial port's traffic control uses Channel 12 in most jurisdictions. What's the channel for port operations?",
  },
];

const CALL_TYPE_SCENARIOS: ReadonlyArray<{
  callType: VhfCallType["id"];
  prompt: string;
}> = [
  {
    callType: "mayday",
    prompt:
      "Engine room fire, extinguisher hasn't held it, crew preparing to abandon ship. Which call?",
  },
  {
    callType: "mayday",
    prompt:
      "Crew member fallen overboard in heavy weather, unable to recover alone. Which call?",
  },
  {
    callType: "mayday",
    prompt:
      "Yacht aground on a falling tide in a breaking sea, hull breached, water rising fast. Which call?",
  },
  {
    callType: "panpan",
    prompt:
      "Engine failure 4 nm offshore in light breeze; drifting toward a shipping lane but no immediate risk. Which call?",
  },
  {
    callType: "panpan",
    prompt:
      "Crew member with chest pain — conscious, breathing, but needs medical advice + possible evacuation. Which call?",
  },
  {
    callType: "panpan",
    prompt:
      "Steering failure offshore, sea state slight, drifting downwind on a long fetch. Which call?",
  },
  {
    callType: "securite",
    prompt:
      "You spot an unlit drifting container in the busy approaches to the Solent. Which call to warn other shipping?",
  },
  {
    callType: "securite",
    prompt:
      "UK Coastguard wants to broadcast a gale warning. Which procedure word?",
  },
  {
    callType: "routine",
    prompt:
      "Calling the marina office to arrange a berth — no urgency, no safety implications. Which call type?",
  },
  {
    callType: "routine",
    prompt:
      "Casual radio check with another yacht in your cruising group. Which call type?",
  },
];

function buildCard(): Card {
  const isChannel = Math.random() < 0.5;
  if (isChannel) {
    const scenario =
      CHANNEL_SCENARIOS[Math.floor(Math.random() * CHANNEL_SCENARIOS.length)]!;
    const answer = VHF_CHANNELS.find((c) => c.number === scenario.channel)!;
    const distractors = shuffled(
      VHF_CHANNELS.filter((c) => c.number !== answer.number),
    ).slice(0, CHOICES_PER_CARD - 1);
    const choices = shuffled([
      { id: String(answer.number), label: `Ch ${answer.number}` },
      ...distractors.map((d) => ({
        id: String(d.number),
        label: `Ch ${d.number}`,
      })),
    ]);
    return {
      kind: "channel",
      prompt: scenario.prompt,
      channelAnswer: answer,
      choices,
    };
  }
  const scenario =
    CALL_TYPE_SCENARIOS[Math.floor(Math.random() * CALL_TYPE_SCENARIOS.length)]!;
  const answer = VHF_CALL_TYPES.find((c) => c.id === scenario.callType)!;
  const choices = shuffled(
    VHF_CALL_TYPES.map((c) => ({ id: c.id, label: c.name })),
  );
  return {
    kind: "call-type",
    prompt: scenario.prompt,
    callTypeAnswer: answer,
    choices,
  };
}

export function Vhf({ onNavigate }: VhfProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: CARDS_PER_SESSION,
      }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (id: string) => {
      if (chosen || result) return;
      const correct =
        card.kind === "channel"
          ? id === String(card.channelAnswer!.number)
          : id === card.callTypeAnswer!.id;
      const attempt: Omit<Attempt, "module"> = {
        char: card.kind,
        ok: correct,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(id);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/mayday")}
      />
    );
  }

  const revealed = chosen !== null;
  const correctId =
    card.kind === "channel"
      ? String(card.channelAnswer!.number)
      : card.callTypeAnswer!.id;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "comms" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · VHF procedure
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          {card.kind === "channel"
            ? "Which VHF channel?"
            : "Which call type?"}
        </h3>
        <p style={{ marginBottom: 12 }}>{card.prompt}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === correctId;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: card.kind === "channel" ? 18 : 14,
                  padding: "12px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === correctId
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            {card.kind === "channel" ? (
              <>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  {chosen === correctId
                    ? `✓ Correct — Ch ${card.channelAnswer!.number}.`
                    : `✗ Not quite — Ch ${card.channelAnswer!.number}.`}
                </p>
                <p className="small" style={{ margin: "6px 0 0" }}>
                  <strong>{card.channelAnswer!.purpose}.</strong>{" "}
                  {card.channelAnswer!.use}
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  {chosen === correctId
                    ? `✓ Correct — ${card.callTypeAnswer!.name}.`
                    : `✗ Not quite — ${card.callTypeAnswer!.name}.`}
                </p>
                <p
                  className="small"
                  style={{
                    margin: "6px 0 0",
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  {card.callTypeAnswer!.procedureWord}
                </p>
                <p className="small" style={{ margin: "6px 0 0" }}>
                  {card.callTypeAnswer!.summary}
                </p>
              </>
            )}
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/mayday")}>
        ← Back to Mayday + VHF
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Mayday + VHF
        </button>
      </div>
    </>
  );
}
